import { useMemo } from "react";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { SM_UIContent } from "@/_self_managed/SM_UIContent";

/**
 * Field-type options for TableDirectImportFieldSelector, in the shape it expects
 * ({ key, comp, value, display, icon, ... }). Shared by every consumer of that
 * component so they can't drift on what's offered.
 *
 * platformConfigs._DirectDataImport.fieldTypeOptions is the SINGLE SOURCE OF
 * TRUTH — an ordered, keyed list authored on the BE
 * (directImportFieldTypeOptions.js) carrying each option's label, icon,
 * grouping and (for Short Text) its block-props preset. Nothing is filtered or
 * relabelled here; the FE renders exactly what the BE offers.
 *
 * Options are identified by `key`, NOT by comp: "Short Text" and "Long Text"
 * are BOTH `LexicalTextEditor` (short text is a Lexical block marked with
 * `props.shortText`, which the editor renders as a plain input), so a comp no
 * longer identifies an option. `value` mirrors `key` because the dropdown selects by value; `comp`
 * stays on the option for the consumers that reason in comp space (swap
 * allowlists, multi-select checks).
 *
 * @param {Object}  [opts]
 * @param {boolean} [opts.includeTitleOption] - prepend the "Document Title"
 *   pseudo-option. Import flow only: it isn't a real comp (it maps to a
 *   TitleInput block via explicitBlockConfig), so the BE doesn't offer it.
 */
export const useDirectImportCompOptions = ({
  includeTitleOption = false,
} = {}) => {
  const platformConfigs = useGetQueryData("platformConfigs");
  const fieldTypeOptions =
    platformConfigs?._DirectDataImport?.fieldTypeOptions;

  return useMemo(() => {
    return [
      ...(includeTitleOption
        ? [
            {
              display: SM_UIContent.SM_DirectDataImport.display.title,
              key: "title",
              comp: "title",
              value: "title",
              icon: "TextT",
            },
          ]
        : []),
      // `|| []` keeps a FE-ahead-of-BE deploy degraded, not broken.
      ...(fieldTypeOptions || []).map((o) => ({ ...o, value: o.key })),
    ];
  }, [fieldTypeOptions, includeTitleOption]);
};
